import { Button, Col, Form, Row } from "antd";
import BieuDo from "components/bieuDo";
import FillterTable from "components/FillterTable";
import { PopUpAddGraphical } from "components/popupAddGraphical";
import RenderForm from "components/renderForm";
import TablePagination from "components/TablePagination";
import dayjs from "dayjs";
import { exportTableToExcelNew } from "helpers";
import { ConverData } from "helpers/dataToAntdSelect";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import {
    getOnlineStoreCategories,
    getOnlineStoreOrderStatusCodes,
    getOnlineStoreProducts,
    getOnlineStoreSuppliers,
} from "services/apiRequest";
import {
    getDataEnergyActivationReport,
    getEnergyActivationReportGraphical,
} from "services/energyActivationRequest";

const rangePresets = [
    {
        label: '7 ngày trước',
        value: [dayjs().add(-7, 'd'), dayjs()],
    },
    {
        label: '14 ngày trước',
        value: [dayjs().add(-14, 'd'), dayjs()],
    },
    {
        label: '30 ngày trước',
        value: [dayjs().add(-30, 'd'), dayjs()],
    },
    {
        label: '90 ngày trước',
        value: [dayjs().add(-90, 'd'), dayjs()],
    },
];

// TYPES.ENERGY_ACTIVATION_REPORT của backend
const metricId = 16;
const dimensionId = 16;

// Gian hàng Lịch Việt Phong Thuỷ
const DEFAULT_SUPPLIER = { id: 5, name: "Gian Hàng Lịch Việt Phong Thuỷ" };
const DEFAULT_SUPPLIER_IDS = [DEFAULT_SUPPLIER.id];

const METRIC_IDS = {
    SO_LUONG_DON_HANG: 1,
    DOANH_THU: 2,
};

/*
Doanh thu chỉ được ghi nhận cho đơn Giao thành công (10003), Hoàn thành (10004)
hoặc Đã thanh toán (10101 - payment_status). Nếu bộ lọc trạng thái không chứa
trạng thái nào trong nhóm này thì cột Doanh thu bị ẩn để tránh hiểu nhầm số liệu.
*/
const REVENUE_STATUS_IDS = [10003, 10004, 10101];

export default function KichHoatNangLuong() {

    const [form] = Form.useForm();

    const [tableFields, setTableFields] = useState({
        metricIds: [METRIC_IDS.SO_LUONG_DON_HANG, METRIC_IDS.DOANH_THU],
        dimensionIds: [1],
    });

    const [dataExport, setDataExport] = useState(null);

    const [graphical, setGraphical] = useState(null);

    const watchForm = Form.useWatch([], form);

    const selectedSupplierIds = watchForm?.supplierIds?.length
        ? watchForm.supplierIds
        : DEFAULT_SUPPLIER_IDS;
    const selectedCategoryIds = watchForm?.categoryIds || [];

    /*
    Ô Nhà cung cấp luôn có sẵn giá trị mặc định (id = 5). Nếu danh sách chưa tải xong hoặc API lỗi,
    antd sẽ hiển thị chính con số 5 thay vì tên gian hàng - nên luôn bổ sung option mặc định.
    */
    const supplierOptions = useMemo(() => {
        const suppliers = dataSuppliers?.data || [];
        if (suppliers.some(item => +item.id === DEFAULT_SUPPLIER.id)) {
            return ConverData(suppliers);
        }
        return ConverData([...suppliers, DEFAULT_SUPPLIER]);
    }, [dataSuppliers]);

    const { data: dataSuppliers } = useQuery(
        ["energyActivation", "suppliers"],
        () => getOnlineStoreSuppliers(),
        { refetchOnWindowFocus: false }
    );

    const { data: dataOrderStatuses } = useQuery(
        ["energyActivation", "orderStatuses"],
        () => getOnlineStoreOrderStatusCodes(),
        { refetchOnWindowFocus: false }
    );

    // Danh mục phụ thuộc nhà cung cấp đang chọn
    const { data: dataCategories } = useQuery(
        ["energyActivation", "categories", selectedSupplierIds],
        () => getOnlineStoreCategories(selectedSupplierIds),
        { refetchOnWindowFocus: false, keepPreviousData: true }
    );

    // Sản phẩm phụ thuộc nhà cung cấp + danh mục đang chọn
    const { data: dataProducts } = useQuery(
        ["energyActivation", "products", selectedSupplierIds, selectedCategoryIds],
        () => getOnlineStoreProducts(selectedSupplierIds, selectedCategoryIds),
        { refetchOnWindowFocus: false, keepPreviousData: true }
    );

    // Bỏ các danh mục không còn hợp lệ sau khi đổi nhà cung cấp
    useEffect(() => {
        if (!dataCategories?.data) {
            return;
        }
        const validIds = dataCategories.data.map(item => +item.id);
        const currentIds = form.getFieldValue("categoryIds") || [];
        const nextIds = currentIds.filter(id => validIds.includes(+id));
        if (nextIds.length !== currentIds.length) {
            form.setFieldValue("categoryIds", nextIds);
        }
    }, [dataCategories]);

    // Bỏ các sản phẩm không còn hợp lệ sau khi đổi nhà cung cấp / danh mục
    useEffect(() => {
        if (!dataProducts?.data) {
            return;
        }
        const validIds = dataProducts.data.map(item => +item.id);
        const currentIds = form.getFieldValue("productIds") || [];
        const nextIds = currentIds.filter(id => validIds.includes(+id));
        if (nextIds.length !== currentIds.length) {
            form.setFieldValue("productIds", nextIds);
        }
    }, [dataProducts]);

    const setUpFormModal = [
        {
            name: 'range-time',
            type: 'range-picker',
            lg: 6,
            propsInput: {
                format: "DD/MM/YYYY",
                presets: rangePresets
            },
            label: 'Thời gian',
        },
        {
            name: 'orderStatusIds',
            type: 'select',
            lg: 6,
            propsInput: {
                showSearch: true,
                allowClear: true,
                mode: 'multiple',
            },
            label: 'Trạng thái đơn hàng',
            options: ConverData(dataOrderStatuses?.data || []),
        },
        {
            name: 'categoryIds',
            type: 'select',
            lg: 6,
            propsInput: {
                showSearch: true,
                allowClear: true,
                mode: 'multiple',
            },
            label: 'Danh mục sản phẩm',
            options: ConverData(dataCategories?.data || []),
        },
        {
            name: 'productIds',
            type: 'select',
            lg: 6,
            propsInput: {
                showSearch: true,
                allowClear: true,
                mode: 'multiple',
            },
            label: 'Sản phẩm',
            options: ConverData(dataProducts?.data || []),
        },
        {
            name: 'supplierIds',
            type: 'select',
            lg: 6,
            propsInput: {
                showSearch: true,
                allowClear: true,
                mode: 'multiple',
            },
            label: 'Nhà cung cấp',
            options: supplierOptions,
        },
        {
            type: "html",
            lg: 3,
            content: (
                <Row className={"flex justify-start items-end content-end h-full"}>
                    <Button type="primary"
                        onClick={() => {
                            form.submit();
                        }}
                    >Áp dụng</Button>
                </Row>
            ),
        },
    ];

    const [whereCondition, setWhereCondition] = useState({
        startTime: dayjs().subtract(30, 'days').format("DD/MM/YYYY"),
        endTime: dayjs().format("DD/MM/YYYY"),
        supplierIds: DEFAULT_SUPPLIER_IDS,
        orderStatusIds: [],
        categoryIds: [],
        productIds: [],
    });

    // Không lọc trạng thái => mặc định đã bao gồm các đơn ghi nhận doanh thu.
    const isRevenueMetricAvailable = useMemo(() => {
        const orderStatusIds = whereCondition?.orderStatusIds || [];
        if (!orderStatusIds.length) {
            return true;
        }
        return orderStatusIds.some(id => REVENUE_STATUS_IDS.includes(+id));
    }, [whereCondition]);

    // Bộ lọc trạng thái không ghi nhận doanh thu => tự bỏ metric Doanh thu khỏi bảng.
    useEffect(() => {
        if (isRevenueMetricAvailable) {
            return;
        }
        setTableFields(prevFields => {
            if (!prevFields.metricIds.includes(METRIC_IDS.DOANH_THU)) {
                return prevFields;
            }
            toast.info("Doanh thu chỉ được tính cho đơn Giao thành công, Hoàn thành hoặc Đã thanh toán nên đã được ẩn khỏi báo cáo.");
            const metricIds = prevFields.metricIds.filter(id => id !== METRIC_IDS.DOANH_THU);
            return {
                ...prevFields,
                metricIds: metricIds.length ? metricIds : [METRIC_IDS.SO_LUONG_DON_HANG],
            };
        });
    }, [isRevenueMetricAvailable]);

    useEffect(() => {

        let initialValues = Object.assign({}, whereCondition);

        const startTime = dayjs(initialValues.startTime, 'DD/MM/YYYY').isValid() ? dayjs(initialValues.startTime, 'DD/MM/YYYY') : null;
        const endTime = dayjs(initialValues.endTime, 'DD/MM/YYYY').isValid() ? dayjs(initialValues.endTime, 'DD/MM/YYYY') : null;

        initialValues['range-time'] = [startTime, endTime];

        form.setFieldsValue(initialValues);
    }, []);

    return (
        <div className="px-7">
            <div className="flex flex-col gap-5 p-7 card">
                <div className="flex items-center justify-items-center justify-between">
                    <h1>
                        <b>THỐNG KÊ KÍCH HOẠT NĂNG LƯỢNG</b>
                    </h1>

                    <div className="flex justify-content-end gap-3">
                        <Button
                            type="primary"
                            onClick={() => {
                                exportTableToExcelNew(dataExport, "THONG_KE_KICH_HOAT_NANG_LUONG");
                            }}
                        >
                            Export
                        </Button>
                        <PopUpAddGraphical
                            metricId={metricId}
                            dimensionId={dimensionId}
                            addDataFields={values => {
                                if (+values?.metric === METRIC_IDS.DOANH_THU && !isRevenueMetricAvailable) {
                                    toast.info("Doanh thu chỉ được tính cho đơn Giao thành công, Hoàn thành hoặc Đã thanh toán. Hãy chọn lại bộ lọc trạng thái đơn hàng.");
                                    return;
                                }
                                const newValues = { ...values };
                                setGraphical(newValues);
                            }}
                        />
                    </div>
                </div>

                <RenderForm
                    onFinish={(values) => {
                        let dataQueries = {};

                        Object.keys(values).map(key => {
                            const item = values[key];
                            if (key == "range-time") {

                                const startTime = dayjs(item[0]).format('DD/MM/YYYY');
                                const endTime = dayjs(item[1]).format('DD/MM/YYYY');

                                dataQueries.startTime = startTime;
                                dataQueries.endTime = endTime;
                            } else {
                                if (item) {
                                    dataQueries[key] = item;
                                } else {
                                    dataQueries[key] = [];
                                }
                            }
                        })

                        // Bỏ trống nhà cung cấp thì quay về gian hàng mặc định.
                        if (!dataQueries.supplierIds?.length) {
                            dataQueries.supplierIds = DEFAULT_SUPPLIER_IDS;
                            form.setFieldValue("supplierIds", DEFAULT_SUPPLIER_IDS);
                        }

                        setWhereCondition(dataQueries);
                    }}
                    form={form}
                    fields={setUpFormModal}
                    footer={<></>}
                />

                {/*
                key ép BieuDo dựng lại khi trạng thái "được phép xem doanh thu" thay đổi,
                để các biểu đồ doanh thu đã thêm trước đó không tiếp tục hiển thị.
                */}
                <BieuDo
                    key={isRevenueMetricAvailable ? "revenue-on" : "revenue-off"}
                    metricId={metricId}
                    dimensionId={dimensionId}
                    whereCondition={whereCondition}
                    apiCallBack={getEnergyActivationReportGraphical}
                    dataFields={[{ metricIds: [METRIC_IDS.SO_LUONG_DON_HANG], dimensionIds: [null] }]}
                    addGraphical={graphical}
                />

                <div className="flex w-full">
                    <Row className="w-full" justify="space-between" gutter={[10, 10]}>
                        <Col span={20}>
                            <TablePagination
                                apiCallBack={getDataEnergyActivationReport}
                                whereCondition={whereCondition}
                                dataFields={tableFields}
                                dataTableChange={values => {
                                    setDataExport(values);
                                }}
                            />
                        </Col>
                        <Col span={4}>
                            <FillterTable
                                key={`${tableFields.metricIds.join('-')}_${tableFields.dimensionIds.join('-')}`}
                                metricId={metricId}
                                dimensionId={dimensionId}
                                dataFields={tableFields}
                                onFieldsChange={values => {
                                    if (!isRevenueMetricAvailable && values.metricIds.includes(METRIC_IDS.DOANH_THU)) {
                                        toast.info("Doanh thu chỉ được tính cho đơn Giao thành công, Hoàn thành hoặc Đã thanh toán. Hãy chọn lại bộ lọc trạng thái đơn hàng.");
                                        const metricIds = values.metricIds.filter(id => id !== METRIC_IDS.DOANH_THU);
                                        setTableFields({
                                            ...values,
                                            metricIds: metricIds.length ? metricIds : [METRIC_IDS.SO_LUONG_DON_HANG],
                                        });
                                        return;
                                    }
                                    setTableFields(values);
                                }}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    )
}
